import { RefObject, useEffect, useRef } from 'react';
import { InputRef } from 'antd';

import { shouldIgnorePlatformShortcut } from '@/utilities/keyboardShortcuts';

interface SlashSearchFocusOptions {
    priority?: number;
}

interface SlashSearchFocusRegistration {
    enabled: boolean;
    inputRef: RefObject<InputRef>;
    priority: number;
    sequence: number;
}

const slashSearchFocusRegistrations = new Map<symbol, SlashSearchFocusRegistration>();
let slashSearchFocusSequence = 0;
let slashSearchFocusListenerAttached = false;

const isVisibleElement = (element: Element) => {
    if (!(element instanceof HTMLElement)) {
        return false;
    }

    const style = window.getComputedStyle(element);

    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
        return false;
    }

    const rect = element.getBoundingClientRect();

    return rect.width > 0 && rect.height > 0;
};

const hasVisibleBlockingOverlay = (focusElement?: HTMLElement | null) => {
    return Array.from(document.querySelectorAll('.ant-modal-wrap, .ant-drawer, .ant-popover, .ant-dropdown')).some(
        (element) => {
            if (element.classList.contains('ant-dropdown-hidden') || element.classList.contains('ant-popover-hidden')) {
                return false;
            }

            if (focusElement && element.contains(focusElement)) {
                return false;
            }

            return isVisibleElement(element);
        },
    );
};

const handleSlashSearchFocus = (event: KeyboardEvent) => {
    if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey || shouldIgnorePlatformShortcut(event)) {
        return;
    }

    const registration = Array.from(slashSearchFocusRegistrations.values())
        .filter(({ enabled, inputRef }) => {
            const focusElement = inputRef.current?.input;

            return (
                enabled && !!focusElement && isVisibleElement(focusElement) && !hasVisibleBlockingOverlay(focusElement)
            );
        })
        .sort((left, right) => right.priority - left.priority || right.sequence - left.sequence)[0];

    if (!registration) {
        return;
    }

    event.preventDefault();
    registration.inputRef.current?.focus({ cursor: 'all' });
};

const attachSlashSearchFocusListener = () => {
    if (slashSearchFocusListenerAttached) {
        return;
    }

    document.addEventListener('keydown', handleSlashSearchFocus, true);
    slashSearchFocusListenerAttached = true;
};

const detachSlashSearchFocusListenerIfUnused = () => {
    if (!slashSearchFocusListenerAttached || slashSearchFocusRegistrations.size > 0) {
        return;
    }

    document.removeEventListener('keydown', handleSlashSearchFocus, true);
    slashSearchFocusListenerAttached = false;
};

export const useSlashSearchFocus = (
    inputRef: RefObject<InputRef>,
    enabled = true,
    { priority = 0 }: SlashSearchFocusOptions = {},
) => {
    const registrationId = useRef(Symbol('slash-search-focus'));

    useEffect(() => {
        const id = registrationId.current;

        slashSearchFocusRegistrations.set(id, {
            enabled,
            inputRef,
            priority,
            sequence: ++slashSearchFocusSequence,
        });
        attachSlashSearchFocusListener();

        return () => {
            slashSearchFocusRegistrations.delete(id);
            detachSlashSearchFocusListenerIfUnused();
        };
    }, [enabled, inputRef, priority]);
};
